/**
 * Auto-attacks (PLAN §3, TC Unit::AttackerStateUpdate; +autoattack: WoW retail rules).
 *
 * Swing timer = weapon speed / (1 + haste); a haste change mid-swing rescales what is left.
 * Players swing only while their attack state is on (`Unit.autoAttack`): right-click / Attack /
 * a weapon ability start it (startAutoAttack, abilityStartsAttack); the target dying, turning
 * friendly or being cleared, death, stealth or Attack again stop it. NPCs swing whenever in combat.
 *
 * Pauses (the timer holds its remaining time): crowd control on the attacker, and hard casts /
 * channels. Out of range or not facing (front 180°): the timer keeps running and a due swing fires
 * the moment the attacker is back in range and facing (`swing.blocked` says why it waits, for the
 * UI). Moving never stops swings; melee range gets the moving leeway from spatial.inMeleeRange.
 *
 * Kinds: 'melee' (one hand, or dual wield with an independent off-hand timer desynced by half a
 * swing at start, 50% damage, DUAL_WIELD_MISS on white swings), 'staff' (a caster's bonk, same
 * rules) and 'ranged' (Auto Shot at `range`, a 0.5 s windup before the first shot).
 */
import type { AbilityDef, School, SimContext, SwingState, Unit } from '../contracts';
import { AUTO_ATTACK_ARC, AUTO_SHOT_ABILITY_ID, AUTO_SHOT_RANGE, AUTO_SHOT_WINDUP_MS, DUAL_WIELD_MISS, MELEE_ABILITY_ID, TICK_MS } from '../contracts';
import { computeAmount } from './amount';
import { fireProcs } from './auras';
import { dealDamage } from './damage';
import { applyGainRules } from './resources';
import { controlState, unitEffectiveStats } from './stats';
import { isHostile } from './util';

function meleeSchool(ctx: SimContext, u: Unit): School {
  if (u.classId) {
    const c = ctx.content.class(u.classId);
    return c?.melee?.school ?? c?.weapon?.school ?? 'physical';
  }
  if (u.npcId) return ctx.content.npc(u.npcId)?.melee?.school ?? 'physical';
  return 'physical';
}

const interval = (speedMs: number, haste: number): number => Math.max(1, Math.round(speedMs / (1 + haste)));

/** A stealth aura (Lurk) is on the unit. */
function stealthed(ctx: SimContext, u: Unit): boolean {
  return u.auras.some((a) => !!ctx.content.aura(a.auraId)?.flags?.stealth);
}

/** The unit's attack target if it can be attacked at all (alive and hostile). */
function attackTarget(ctx: SimContext, u: Unit): Unit | undefined {
  const t = u.targetId ? ctx.world.units[u.targetId] : undefined;
  return t && t.alive && !t.departed && t.id !== u.id && isHostile(u, t) ? t : undefined;
}

/** Target within weapon reach: melee range (with moving leeway) or Auto Shot range. */
export function inAttackRange(ctx: SimContext, u: Unit, t: Unit): boolean {
  if (u.jump?.air?.glide) return false; // +glide: no swings from the glider
  const sw = u.swing;
  if (sw?.kind === 'ranged') return ctx.sys.spatial.inRange(ctx.world, u.id, t.id, sw.range ?? AUTO_SHOT_RANGE);
  return ctx.sys.spatial.inMeleeRange(ctx.world, u.id, t.id);
}

/**
 * Turn the unit's attack state on (at its current target). Starting never resets a running timer;
 * a ready one swings this tick. Auto Shot waits AUTO_SHOT_WINDUP_MS; the off hand starts half a
 * swing behind the main hand. Returns true when auto-attack is (now) on.
 */
export function startAutoAttack(ctx: SimContext, u: Unit): boolean {
  const sw = u.swing;
  if (!sw || !u.meleeDamage || !u.alive) return false;
  if (!attackTarget(ctx, u)) return false;
  if (u.autoAttack) return true;
  u.autoAttack = true;
  const now = ctx.now;
  const haste = unitEffectiveStats(ctx, u).haste;
  sw.haste = haste;
  if (sw.kind === 'ranged') sw.nextAt = Math.max(sw.nextAt, now + AUTO_SHOT_WINDUP_MS);
  if (sw.oh) sw.oh.nextAt = Math.max(sw.oh.nextAt, Math.max(sw.nextAt, now) + Math.round(interval(sw.oh.speedMs, haste) / 2));
  return true;
}

/** Turn the unit's attack state off (the timer keeps running: toggling never resets it). */
export function stopAutoAttack(u: Unit): void {
  if (u.autoAttack) u.autoAttack = false;
  if (u.swing?.blocked) u.swing.blocked = null;
}

/** Does using `ab` start this unit's auto-attack? (AbilityDef.startsAttack, else the class weapon rule.) */
export function abilityStartsAttack(u: Unit, ab: AbilityDef): boolean {
  if (ab.startsAttack !== undefined) return ab.startsAttack;
  const sw = u.swing;
  if (!sw || sw.kind === 'staff') return false; // casters' spells never start it
  if (ab.melee) return true;
  const hits = ab.effects.some((e) => e.type === 'damage');
  if (ab.targeting === 'enemy') return hits;
  // Self-centred weapon sweeps (Wide Arc, Bladefan) start melee swings at the current target.
  if (ab.targeting === 'none') return (sw.kind ?? 'melee') === 'melee' && ab.effects.some((e) => e.type === 'damage' && e.to === 'area');
  return false;
}

/** Casting hook: a player's weapon ability starts auto-attack at its hostile target. */
export function startAttackFromAbility(ctx: SimContext, u: Unit, ab: AbilityDef): void {
  if (u.kind !== 'player' || u.autoAttack || !abilityStartsAttack(u, ab)) return;
  startAutoAttack(ctx, u);
}

/** Rescale what is left of each running interval to the new haste. */
function rescaleHaste(sw: SwingState, haste: number, now: number): void {
  const old = sw.haste;
  sw.haste = haste;
  if (old === undefined || old === haste) return;
  const f = (1 + old) / (1 + haste);
  if (sw.nextAt > now) sw.nextAt = now + Math.max(1, Math.round((sw.nextAt - now) * f));
  if (sw.oh && sw.oh.nextAt > now) sw.oh.nextAt = now + Math.max(1, Math.round((sw.oh.nextAt - now) * f));
}

/** Hold the timers (CC, hard cast): what is left stays left; a due swing stays due. */
function pause(sw: SwingState, now: number): void {
  if (sw.nextAt > now) sw.nextAt += TICK_MS;
  if (sw.oh && sw.oh.nextAt > now) sw.oh.nextAt += TICK_MS;
}

function swingOnce(ctx: SimContext, u: Unit, t: Unit, hand: 'mh' | 'oh' | 'shot'): void {
  const sw = u.swing!;
  const shot = hand === 'shot';
  const abilityId = shot ? AUTO_SHOT_ABILITY_ID : MELEE_ABILITY_ID;
  let amount = computeAmount(ctx, u.meleeDamage!, u.id, t.id);
  if (hand === 'oh') amount *= sw.oh?.mult ?? 0.5;
  const r = dealDamage(ctx, {
    sourceId: u.id,
    targetId: t.id,
    abilityId,
    school: meleeSchool(ctx, u),
    amount,
    avoidable: !shot,
    ...(u.kind === 'player' ? { swing: hand } : {}),
    ...(sw.oh ? { missBonus: DUAL_WIELD_MISS } : {}),
  });
  const landed = r.hitType === 'hit' || r.hitType === 'crit' || r.hitType === 'block';
  if (landed) applyGainRules(ctx, u.id, 'meleeSwingLanded', abilityId);
  fireProcs(ctx, u.id, 'meleeSwing', t.id, abilityId, 0);
  if (landed) fireProcs(ctx, u.id, 'meleeSwingLanded', t.id, abilityId, 0); // +lead (F6)
}

export function tickMelee(ctx: SimContext): void {
  const w = ctx.world;
  const now = ctx.now;
  for (const id of w.unitOrder) {
    const u = w.units[id];
    const sw = u?.swing;
    if (!u || !sw || !u.meleeDamage) continue;
    let t: Unit | undefined;
    if (u.kind === 'player') {
      if (!u.autoAttack) continue;
      t = u.alive && !stealthed(ctx, u) ? attackTarget(ctx, u) : undefined;
      if (!t) {
        stopAutoAttack(u);
        continue;
      }
    } else {
      if (!u.alive || !u.inCombat) continue;
      t = attackTarget(ctx, u);
      if (!t) continue;
    }
    rescaleHaste(sw, unitEffectiveStats(ctx, u).haste, now);
    // Hard casts / channels and crowd control hold the swing timer.
    if (u.cast || !controlState(ctx, u.id).canAttack) {
      pause(sw, now);
      continue;
    }
    const mhDue = now >= sw.nextAt;
    const ohDue = !!sw.oh && now >= sw.oh.nextAt;
    if (!mhDue && !ohDue) continue;
    // Due: wait (timer running) until in range and facing.
    const blocked = !inAttackRange(ctx, u, t) ? 'range' : !ctx.sys.spatial.isInFront(w, u.id, t.id, AUTO_ATTACK_ARC) ? 'facing' : null;
    if (blocked) {
      if (u.kind === 'player') sw.blocked = blocked;
      continue;
    }
    if (sw.blocked) sw.blocked = null;
    const haste = sw.haste ?? 0;
    if (mhDue) {
      sw.nextAt = now + interval(sw.speedMs, haste);
      swingOnce(ctx, u, t, sw.kind === 'ranged' ? 'shot' : 'mh');
    }
    if (ohDue && sw.oh && u.alive && t.alive) {
      sw.oh.nextAt = now + interval(sw.oh.speedMs, haste);
      swingOnce(ctx, u, t, 'oh');
    }
  }
}
